import PropTypes from 'prop-types';
import React, { useDeferredValue, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { SCLoader, SCButton } from 'rollup-allspark';
import {
  COMPARISON_TYPE,
  EXPORT_TYPE,
  ITEM_BREAKDOWN_TABS,
} from '../../../config/constants/analytics';
import SectionCard from '../components/SectionCard';
import useAnalytics from '../../../hooks/useAnalytics';
import { cn, isEmpty, printf } from '../../../utils';
import {
  clampPeriodToDays,
  MENU_REPORT_MAX_DAYS,
} from '../../../utils/nativeAnalyticsUtils';
import {
  getCSV,
  getXLSX,
  loadItemBreakdownData,
  updateEntity,
} from '../actions';
import { EXPORT_ITEM_LEVEL_BREAKDOWN } from '../sagaMap';
import { makeSelectProperty, makeSelectXlsxDownloading } from '../selectors';
import ItemBreakDownTable from './ItemBreakDownTable';
import ItemBreakDownTableWithProvider from './ItemBreakDownTableWithProvider';

const ItemBreakDown = ({
  getItemBreakdownData,
  itemBreakDownData,
  itemBreakDownDataLoading,
  selectedComparisonType,
  analyticsProviders,
  downloadCSV,
  isDownloadingCSV,
  isXlsxDownloading,
  downloadXLSX,
  updateField,
}) => {
  // HOOKS
  const [selectedTab, setSelectedTab] = useState(ITEM_BREAKDOWN_TABS[0]);
  const [tableData, setTableData] = useState([]);
  const [activeDownloadKey, setActiveDownloadKey] = useState(null);
  const comparisonType = useDeferredValue(selectedComparisonType);

  useEffect(() => {
    if (!isDownloadingCSV && !isXlsxDownloading) {
      setActiveDownloadKey(null);
    }
  }, [isDownloadingCSV, isXlsxDownloading]);

  const { getAnalyticsParams } = useAnalytics();

  useEffect(() => {
    setTableData(itemBreakDownData || null);
  }, [itemBreakDownData]);

  // HANDLERS
  const getFilteredItemBreakdownData = id => {
    const params = { ...getAnalyticsParams() };
    params.activeTab = id;
    getItemBreakdownData(params);
  };

  const handleChangeTab = tab => {
    updateField({
      selectedMenuReportType: tab,
    });
    setSelectedTab(tab);
    getFilteredItemBreakdownData(tab.id);
  };

  const handleDownloadClicked = exportType => {
    setActiveDownloadKey(exportType);
    const url = printf(EXPORT_ITEM_LEVEL_BREAKDOWN.url, selectedTab.key);
    const fileName = `${selectedTab.title} Breakdown`;
    const { params: clampedParams } = clampPeriodToDays(getAnalyticsParams());
    const params = { ...clampedParams, export_option: exportType };

    if (exportType === EXPORT_TYPE.CSV) {
      downloadCSV({
        ...EXPORT_ITEM_LEVEL_BREAKDOWN,
        url,
        params,
        fileName,
      });
    } else if (exportType === EXPORT_TYPE.XLSX) {
      downloadXLSX({
        ...EXPORT_ITEM_LEVEL_BREAKDOWN,
        url,
        params,
        fileName,
      });
    }
  };

  const isAnyExportInProgress =
    (isDownloadingCSV || isXlsxDownloading) && activeDownloadKey;
  const isExportInProgress = Boolean(isAnyExportInProgress);
  const areExportButtonsDisabled =
    itemBreakDownDataLoading || isExportInProgress;
  const isTabClickBlocked = itemBreakDownDataLoading || isExportInProgress;

  const isTabDisabled = tab =>
    isExportInProgress ||
    (itemBreakDownDataLoading && tab.id !== selectedTab.id);

  // RENDERS
  // Tabs
  const tabsEl = (() => {
    const tabs = ITEM_BREAKDOWN_TABS.map(ct => (
      <div
        key={ct.id}
        role="presentation"
        className={cn({
          'py-2 rounded-md text-sm font-medium w-full text-center transition-colors': true,
          'bg-white text-primary shadow-sm': selectedTab.id === ct.id,
          'text-grey-darker hover:text-black':
            selectedTab.id !== ct.id && !isTabDisabled(ct),
          'text-grey-darker opacity-50 cursor-not-allowed': isTabDisabled(ct),
        })}
        onClick={() => {
          if (isTabClickBlocked || isTabDisabled(ct)) return;
          handleChangeTab(ct);
        }}
      >
        {ct.title}
      </div>
    ));

    return (
      <div className="flex items-center justify-center gap-x-1 p-1 bg-grey-light rounded-md">
        {tabs}
      </div>
    );
  })();

  // loading placeholder
  const renderLoadingPlaceholder = (
    <div className="w-full h-20">
      <SCLoader lineWidth={5} diameter={50} />
    </div>
  );

  // AggregatedTable
  const aggregatedTable = itemBreakDownDataLoading ? (
    renderLoadingPlaceholder
  ) : (
    <ItemBreakDownTable
      tableData={tableData}
      currentTab={selectedTab}
      itemBreakDownDataLoading={itemBreakDownDataLoading}
    />
  );

  // providerWiseTable
  const providerWiseTable = itemBreakDownDataLoading ? (
    renderLoadingPlaceholder
  ) : (
    <ItemBreakDownTableWithProvider
      tableData={tableData}
      currentTab={selectedTab}
      analyticsProviders={analyticsProviders}
      itemBreakDownDataLoading={itemBreakDownDataLoading}
    />
  );

  const tableEl =
    comparisonType === COMPARISON_TYPE.AGGREGATOR
      ? providerWiseTable
      : aggregatedTable;

  const menuClamp = clampPeriodToDays(getAnalyticsParams());
  const calloutEl = (
    <div className="flex items-center gap-x-2 rounded-md border border-warning bg-warning-lighter px-3 py-2 text-xs font-semibold text-black">
      <span aria-hidden>⚠️</span>
      <span>
        {menuClamp.clamped
          ? `Menu reports are limited to a ${MENU_REPORT_MAX_DAYS}-day window — showing ${menuClamp.from} → ${menuClamp.to}. Narrow the date range to pick a different week.`
          : `Menu reports cover up to the last ${MENU_REPORT_MAX_DAYS} days of the selected range.`}
      </span>
    </div>
  );

  const isExportButtonLoading = exportType =>
    activeDownloadKey === exportType &&
    (exportType === EXPORT_TYPE.CSV ? isDownloadingCSV : isXlsxDownloading);

  const renderExportButton = exportType => {
    const isLoading = isExportButtonLoading(exportType);
    const label = exportType.toUpperCase();

    return (
      <SCButton
        key={exportType}
        variant="secondary"
        className="h-6 rounded-lg bg-grey-light text-black hover:bg-grey gap-x-1 w-auto flex items-center px-4 text-xs font-medium"
        action={() => handleDownloadClicked(exportType)}
        disabled={areExportButtonsDisabled}
      >
        <div className="flex items-center gap-x-1 font-medium min-w-[42px] justify-center">
          {isLoading ? (
            <SCLoader lineWidth={2} diameter={16} />
          ) : (
            <>
              <i className="kt-download text-lg" />
              <span>{label}</span>
            </>
          )}
        </div>
      </SCButton>
    );
  };

  const renderDownloadActions = !isEmpty(tableData?.analytics) ? (
    <div className="flex items-center gap-x-2">
      {renderExportButton(EXPORT_TYPE.CSV)}
      {renderExportButton(EXPORT_TYPE.XLSX)}
    </div>
  ) : null;

  return (
    <SectionCard
      title="Menu Report"
      metricKey="menuReportWindow"
      actions={renderDownloadActions}
    >
      <div className="mb-4">{calloutEl}</div>
      <div className="mb-4">{tabsEl}</div>
      {tableEl}
    </SectionCard>
  );
};

ItemBreakDown.propTypes = {
  getItemBreakdownData: PropTypes.func,
  itemBreakDownData: PropTypes.any,
  itemBreakDownDataLoading: PropTypes.bool,
  selectedComparisonType: PropTypes.string,
  analyticsProviders: PropTypes.array,
  downloadCSV: PropTypes.func,
  isDownloadingCSV: PropTypes.bool,
  isXlsxDownloading: PropTypes.bool,
  downloadXLSX: PropTypes.func,
  updateField: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  itemBreakDownData: makeSelectProperty('itemBreakDownData'),
  itemBreakDownDataLoading: makeSelectProperty('itemBreakDownDataLoading'),
  selectedComparisonType: makeSelectProperty('selectedComparisonType'),
  analyticsProviders: makeSelectProperty('analyticsProviders'),
  isDownloadingCSV: makeSelectProperty('isDownloadingCSV'),
  isXlsxDownloading: makeSelectXlsxDownloading(),
});

const mapDispatchToProps = dispatch => ({
  getItemBreakdownData: params => dispatch(loadItemBreakdownData(params)),
  downloadCSV: params => dispatch(getCSV(params)),
  downloadXLSX: params => dispatch(getXLSX(params)),
  updateField: params => dispatch(updateEntity(params)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(ItemBreakDown);
